import { HOMEWORK_DEFAULT_TITLE_KEYS } from '@cc/schema';
import { useCustom, useTranslate } from '@refinedev/core';
import { Button, Card, Select, Space, Table, Tag, Typography } from 'antd';
import { useMemo, useState } from 'react';
import {
  EmptyState,
  ResultCard,
  ToolAssignButton,
  ToolSection,
  fmtDay,
  fmtDayShort,
} from '../../components/ToolSection';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { useRefreshClientPage } from '../clients/refreshClientPage';
import type { AssignPreset } from '../homework/AssignHomeworkDrawer';
import { FaBehaviorDrawer } from './FaBehaviorDrawer';
import { FaPlanDrawer } from './FaPlanDrawer';
import { FaRetireDrawer } from './FaRetireDrawer';
import {
  CHAIN_ANALYSIS_TOOL,
  DEFAULT_ASSIGN_WEEKS,
  MAX_ASSIGN_WEEKS,
  ROSTER_FIELDS,
  SUMMARY_FIELDS,
  changeDaysArgs,
  fromDiaryCard,
  orderedRoster,
  periodHeading,
  recurringGroups,
  selectedTarget,
  urgeFigure,
  type RosterRow,
  type SummaryPeriod,
  type SummaryRow,
  type SummaryWeek,
} from './model';

/**
 * E48 · Functional analysis — "Client Hub — Functional analysis" (design PORTAL-SECTIONS-2026-10-22 §2.3). The behavior's card comes
 * first, with its own buttons (Edit · Plan a change · Retire — each opens a side drawer); then the Records; then What recurs. One
 * Assign in the header (the records, for the behavior on screen). An empty client gets one line and one button.
 *
 * Visible through a relation with VIEW permission (the hub's verdict is the caller's — `show.tsx`; the server refuses with
 * FA_NOT_YOUR_CLIENT otherwise and the message is shown as the API sent it). DISPLAY ONLY: counts and the client's own words —
 * no verdict, no advice. The soft note under `threshold` records is a WARNING in the Plan drawer, never a block.
 */
type Props = { clientId: string; clientName: string; locale: 'en' | 'ru' };
type Tx = (key: string, options?: Record<string, unknown>) => string;

export const ClientFunctionalAnalysisTab = ({
  clientId,
  clientName,
  locale,
}: Props) => {
  const t = useTranslate() as unknown as Tx;
  const refreshClientPage = useRefreshClientPage();

  // the client's own time zone — their days are theirs
  const zoneQuery = useCustom<{ _id: string; timezone?: string | null }>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'client',
      fields: ['_id', 'timezone'],
      variables: { id: { name: 'id', type: 'ID', value: clientId } },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const zoneSettled = zoneQuery.isFetched || zoneQuery.isError;
  const clientZone =
    zoneQuery.data?.data?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  // ── the roster and the selected behaviour ─────────────────────────────────────────────────
  const rosterQuery = useCustom<RosterRow[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'clientTargetBehaviors',
      fields: ROSTER_FIELDS,
      variables: { subject: { name: 'subject', type: 'ID!', value: clientId } },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const roster = useMemo(
    () => orderedRoster(rosterQuery.data?.data ?? []),
    [rosterQuery.data],
  );
  const [wanted, setWanted] = useState<string | null>(null);
  const selected = selectedTarget(roster, wanted);
  const targetId = selected?.targetId ?? null;

  const summaryQuery = useCustom<SummaryRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'functionalAnalysisSummary',
      fields: SUMMARY_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID!', value: clientId },
        targetId: { name: 'targetId', type: 'ID!', value: targetId },
        timeZone: { name: 'timeZone', type: 'String', value: clientZone },
      },
    },
    queryOptions: {
      enabled: Boolean(clientId) && Boolean(targetId) && zoneSettled,
      retry: false,
      keepPreviousData: false,
    },
  });
  const summary =
    summaryQuery.data?.data && summaryQuery.data.data.targetId === targetId
      ? summaryQuery.data.data
      : null;
  const refetchAll = () => {
    rosterQuery.refetch();
    summaryQuery.refetch();
  };
  /** After a saved drawer: re-read this section and the page's pill/digest. */
  const afterSave = () => {
    refetchAll();
    refreshClientPage('assign');
  };

  const loadError = rosterQuery.error ?? summaryQuery.error;
  const loadMessage = loadError
    ? graphqlErrorInfo(loadError).message ?? t('fa.errors.FA_TARGET_UNKNOWN')
    : null;

  const toolName = (tool?: string | null) =>
    tool ? t(`fa.tool.${tool}`) : null;

  // ── the drawers ───────────────────────────────────────────────────────────────────────────
  const [behaviorOpen, setBehaviorOpen] = useState<
    null | { existing: SummaryRow | null }
  >(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);

  const closed = Boolean(selected?.retired);
  const name = selected?.name ?? '';

  // ── Assign: the records, for the behavior on screen ───────────────────────────────────────
  const openBehaviors = roster.filter((r) => !r.retired);
  const assignPreset: AssignPreset = {
    kind: 'tool',
    target: CHAIN_ANALYSIS_TOOL,
    targets: [CHAIN_ANALYSIS_TOOL],
    defaults: {
      [CHAIN_ANALYSIS_TOOL]: {
        title: (refLabel) =>
          // Q-ru-3 A: left as it is, only the behavior's name is stored — each reader renders the sentence in its language
          t(HOMEWORK_DEFAULT_TITLE_KEYS.chainAnalysis, { name: refLabel ?? name }),
        reminderPolicy: 'default',
        deadline: {
          shape: 'repeat',
          frequency: 'weekly',
          hour: 18,
          minute: 0,
          startInDays: 1,
          count: DEFAULT_ASSIGN_WEEKS,
          countMax: MAX_ASSIGN_WEEKS,
          countUnit: 'weeks',
        },
        ref: {
          label: t('fa.hub.pick'),
          options: openBehaviors.map((r) => ({
            value: r.targetId,
            label: r.name,
          })),
          value: selected && !selected.retired ? selected.targetId : undefined,
          requiredMessage: t('fa.assign.refRequired'),
        },
      },
    },
  };
  const assignDisabledReason = !selected
    ? t('fa.assign.needsBehavior')
    : closed
      ? t('fa.assign.closed')
      : undefined;

  // ── pieces ────────────────────────────────────────────────────────────────────────────────
  const weekColumns = (periodIndex: number) => [
    {
      title: t('fa.hub.weeks'),
      dataIndex: 'weekStart',
      render: (d: string) =>
        t('fa.hub.weekOf', { date: fmtDayShort(d, locale) }),
    },
    {
      title: t('fa.hub.did'),
      dataIndex: 'done',
      render: (n: number, w: SummaryWeek) => (
        <span data-testid={`fa-period-${periodIndex}-week-${w.weekStart}-done`}>
          {n}
        </span>
      ),
    },
    {
      title: t('fa.hub.urgeOnly'),
      dataIndex: 'urgeOnly',
      render: (n: number, w: SummaryWeek) => (
        <span
          data-testid={`fa-period-${periodIndex}-week-${w.weekStart}-urge-only`}
        >
          {n}
        </span>
      ),
    },
    {
      title: t('fa.hub.meanUrge', { n: '' }).trim(),
      dataIndex: 'meanUrge',
      render: (n: number | null | undefined) => urgeFigure(n) ?? '—',
    },
  ];

  const periodCard = (p: SummaryPeriod, i: number) => {
    const heading = periodHeading(p);
    const mean = urgeFigure(p.totals.meanUrge);
    return (
      <Card
        key={`${p.kind}-${p.changeId ?? i}`}
        size="small"
        type="inner"
        title={
          heading.key === 'after' && heading.from
            ? t('fa.hub.after', { date: fmtDay(heading.from, locale) })
            : t('fa.hub.before')
        }
        data-testid={`fa-period-${i}`}
        data-kind={p.kind}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space wrap data-testid={`fa-period-${i}-totals`}>
            <span data-testid={`fa-period-${i}-done`}>
              {t('fa.hub.did')} {p.totals.done}
            </span>
            <span data-testid={`fa-period-${i}-urge-only`}>
              {t('fa.hub.urgeOnly')} {p.totals.urgeOnly}
            </span>
            {mean ? <span>{t('fa.hub.meanUrge', { n: mean })}</span> : null}
            {p.totals.perWeek !== null && p.totals.perWeek !== undefined ? (
              <span>
                {t('fa.hub.perWeek', { n: urgeFigure(p.totals.perWeek) })}
              </span>
            ) : null}
          </Space>
          {p.weeks.length ? (
            <Table
              size="small"
              pagination={false}
              rowKey="weekStart"
              columns={weekColumns(i)}
              dataSource={p.weeks}
              scroll={{ x: 'max-content' }}
            />
          ) : null}
          {p.changeDays && p.changeDays.marked > 0 ? (
            <Typography.Text
              type="secondary"
              data-testid={`fa-period-${i}-days`}
            >
              {t(
                'fa.hub.changeDays',
                changeDaysArgs({
                  doneDays: p.changeDays,
                  markedDays: p.changeDays.marked,
                }),
              )}
            </Typography.Text>
          ) : null}
        </Space>
      </Card>
    );
  };

  const diaryDays = (summary?.days ?? []).filter(fromDiaryCard);
  const groups = recurringGroups(summary?.recurring);
  const empty = !roster.length && !loadMessage && !rosterQuery.isLoading;

  const setBehaviorButton = (testId: string, primary: boolean) => (
    <Button
      data-testid={testId}
      onClick={() => setBehaviorOpen({ existing: null })}
      type={primary ? 'primary' : 'default'}
    >
      {t('fa.hub.set.add')}
    </Button>
  );

  const actions = (
    <>
      {empty ? null : setBehaviorButton('fa-hub-set-open', false)}
      <ToolAssignButton
        clientId={clientId}
        clientName={clientName}
        preset={assignPreset}
        onAssigned={refetchAll}
        disabledReason={assignDisabledReason}
      />
    </>
  );

  const toolbar = roster.length ? (
    <>
      <Typography.Text type="secondary">{t('fa.hub.pick')}</Typography.Text>
      <Select
        data-testid="fa-hub-select"
        style={{ flex: '1 1 260px', maxWidth: 480 }}
        value={targetId ?? undefined}
        onChange={(v: string) => setWanted(v)}
        options={roster.map((r) => ({
          value: r.targetId,
          label: (
            <span data-testid={`fa-hub-option-${r.targetId}`}>
              {r.name}
              {r.addedByClient ? ` · ${t('fa.hub.byClient')}` : ''}
              {r.retired ? ` · ${t('fa.hub.closed')}` : ''}
              {` · ${t('fa.my.recordsCount', { count: r.records })}`}
            </span>
          ),
        }))}
      />
    </>
  ) : undefined;

  const behaviorCard = selected ? (
    <ResultCard
      testId="fa-formulation"
      role="result"
      title={
        <Space wrap data-testid="fa-set-current">
          <Typography.Text strong data-testid="fa-formulation-target">
            {selected.name}
          </Typography.Text>
          {selected.addedByClient ? <Tag>{t('fa.hub.byClient')}</Tag> : null}
          {selected.retired ? (
            <Tag data-testid="fa-set-closed">{t('fa.hub.closed')}</Tag>
          ) : null}
        </Space>
      }
      actions={
        summary && !closed ? (
          <>
            <Button
              size="small"
              data-testid="fa-hub-set-edit"
              onClick={() => setBehaviorOpen({ existing: summary })}
            >
              {t('fa.hub.set.edit')}
            </Button>
            <Button
              size="small"
              data-testid="fa-plan-open"
              onClick={() => setPlanOpen(true)}
            >
              {t('fa.hub.planOpen')}
            </Button>
            <Button
              size="small"
              danger
              data-testid="fa-retire-open"
              onClick={() => setRetireOpen(true)}
            >
              {t('fa.hub.retireOpen')}
            </Button>
          </>
        ) : null
      }
    >
      {summary ? (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          {summary.target.description ? (
            <Typography.Text>{summary.target.description}</Typography.Text>
          ) : null}
          {/* Q-e48f-1 A (2026-10-24): every chosen function, one line each, in the list's order */}
          <div
            data-testid="fa-formulation-function"
            data-function={summary.target.functionHypotheses.join(' ')}
          >
            <Typography.Text>{t('fa.hub.function')}:</Typography.Text>
            {summary.target.functionHypotheses.map((f) => (
              <Typography.Text
                key={f}
                data-testid={`fa-formulation-function-${f}`}
                style={{ display: 'block' }}
              >
                {t(`fa.function.${f}`)} — {t(`fa.functionLine.${f}`)}
              </Typography.Text>
            ))}
          </div>
          <Typography.Text type="secondary">
            {t('fa.hub.functionNote')}
          </Typography.Text>
          {summary.target.retired ? (
            <Typography.Text
              type="secondary"
              data-testid="fa-formulation-closed"
            >
              {t('fa.hub.closed')}
              {summary.target.retiredReason
                ? ` — ${summary.target.retiredReason}`
                : ''}
            </Typography.Text>
          ) : null}
          <Typography.Text data-testid="fa-formulation-measure">
            {t('fa.hub.measureLine', {
              measure: t(`fa.measure.${summary.target.measure}`),
              weeks: t('fa.hub.baselineWeeks', {
                count: summary.target.baselineWeeks,
              }),
            })}
          </Typography.Text>
          <Typography.Text data-testid="fa-formulation-records">
            {t('fa.hub.records')}:{' '}
            {t('fa.hub.recordsLine', {
              chains: summary.chains,
              urges: summary.urgesOnly,
            })}
          </Typography.Text>
        </Space>
      ) : (
        <Typography.Text type="secondary">
          {t('pages.toolSection.loading')}
        </Typography.Text>
      )}
    </ResultCard>
  ) : null;

  const recordsCard = summary ? (
    <ResultCard
      testId="fa-periods"
      role="result"
      title={t('fa.hub.recordsTitle')}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {summary.periods.map(periodCard)}
        {summary.changes.length ? (
          <Space
            direction="vertical"
            style={{ width: '100%' }}
            data-testid="fa-changes"
          >
            <Typography.Text strong>{t('fa.hub.changes')}</Typography.Text>
            {summary.changes.map((c) => (
              <Space
                key={c.changeId}
                direction="vertical"
                size={0}
                data-testid={`fa-change-${c.changeId}`}
              >
                <Typography.Text data-testid={`fa-change-${c.changeId}-what`}>
                  {c.whatChanges}
                </Typography.Text>
                {c.expectedEffect ? (
                  <Typography.Text type="secondary">
                    {c.expectedEffect}
                  </Typography.Text>
                ) : null}
                <Space size={8}>
                  <Typography.Text type="secondary">
                    {fmtDay(c.startsOn, locale)}
                  </Typography.Text>
                  {c.byClient ? (
                    <Tag data-testid={`fa-change-${c.changeId}-by-client`}>
                      {t('fa.hub.byClientChange')}
                    </Tag>
                  ) : null}
                </Space>
                <Typography.Text
                  type="secondary"
                  data-testid={`fa-change-${c.changeId}-days`}
                >
                  {c.markedDays > 0
                    ? t('fa.hub.changeDays', changeDaysArgs(c))
                    : t('fa.hub.noChangeDays')}
                </Typography.Text>
              </Space>
            ))}
          </Space>
        ) : null}
        {diaryDays.length ? (
          <Space wrap data-testid="fa-diary-days">
            {diaryDays.map((d) => (
              <Tag key={d.date} data-testid={`fa-diary-day-${d.date}`}>
                {fmtDayShort(d.date, locale)} · {t('fa.hub.fromDiaryCard')}
              </Tag>
            ))}
          </Space>
        ) : null}
      </Space>
    </ResultCard>
  ) : null;

  const recursCard = summary ? (
    <ResultCard
      testId="fa-recurring"
      role="result"
      title={t('fa.hub.recurs')}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div data-testid="fa-suggestion">
          <Typography.Text strong>{t('fa.hub.suggestion')}: </Typography.Text>
          {summary.suggestion ? (
            <Typography.Text data-testid="fa-suggestion-line">
              {t('fa.hub.suggestionLine', {
                instead: summary.suggestion.instead,
                times: summary.suggestion.times,
              })}
              {summary.suggestion.tool ? (
                <Tag style={{ marginLeft: 8 }}>
                  {toolName(summary.suggestion.tool)}
                </Tag>
              ) : null}
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary">
              {t('fa.hub.noSuggestion')}
            </Typography.Text>
          )}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 16,
          }}
        >
          {groups.map((g) => (
            <div key={g.id} data-testid={`fa-recurring-${g.id}`}>
              <Typography.Text strong>{t(g.headingKey)}</Typography.Text>
              {g.hintKey ? (
                <Typography.Text type="secondary" style={{ display: 'block' }}>
                  {t(g.hintKey)}
                </Typography.Text>
              ) : null}
              {g.rows.map((r) => (
                <div
                  key={r.text}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                >
                  <span>
                    {r.text}
                    {r.tool ? (
                      <Tag style={{ marginLeft: 8 }}>{toolName(r.tool)}</Tag>
                    ) : null}
                  </span>
                  <span data-testid={`fa-recurring-${g.id}-count-${r.text}`}>
                    {r.count}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Space>
    </ResultCard>
  ) : null;

  return (
    <>
      <ToolSection
        testId="fa-hub-tab"
        title={t('fa.hub.title')}
        purpose={t('fa.hub.purpose')}
        actions={actions}
        toolbar={toolbar}
        loadError={loadMessage}
        loadErrorTestId="fa-hub-error"
        loading={rosterQuery.isLoading && !rosterQuery.data}
        loadingText={`${t('fa.hub.title')}…`}
      >
        {empty ? (
          <EmptyState
            text={t('fa.hub.empty')}
            primary={
              <Button
                type="primary"
                data-testid="fa-empty-set-open"
                onClick={() => setBehaviorOpen({ existing: null })}
              >
                {t('fa.hub.set.add')}
              </Button>
            }
          />
        ) : null}
        {behaviorCard}
        {recordsCard}
        {recursCard}
      </ToolSection>

      <FaBehaviorDrawer
        open={behaviorOpen !== null}
        clientId={clientId}
        existing={behaviorOpen?.existing ?? null}
        onClose={() => setBehaviorOpen(null)}
        onSaved={(id) => {
          setBehaviorOpen(null);
          if (id) setWanted(id);
          afterSave();
        }}
      />
      {summary && targetId ? (
        <FaPlanDrawer
          open={planOpen}
          clientId={clientId}
          targetId={targetId}
          summary={summary}
          onClose={() => setPlanOpen(false)}
          onSaved={() => {
            setPlanOpen(false);
            afterSave();
          }}
        />
      ) : null}
      <FaRetireDrawer
        open={retireOpen}
        clientId={clientId}
        targetId={targetId}
        onClose={() => setRetireOpen(false)}
        onSaved={() => {
          setRetireOpen(false);
          afterSave();
        }}
      />
    </>
  );
};
